import {
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import {
  type IntercomProviderProps,
  IntercomProvider as _IntercomProvider,
  useIntercom as _useIntercom,
} from 'react-use-intercom';

const INTERCOM_APP_ID = 'ec8spyh1';

type C = {
  unreadMessagesCount: number;
  setUnreadMessagesCount: (value: number) => void;
  isBooted: boolean;
  setIsBooted: (value: boolean) => void;
  userHash: string | undefined;
  setUserHash: (value: string | undefined) => void;
};

export const intercomContext = createContext<C>({
  unreadMessagesCount: 0,
  setUnreadMessagesCount: () => undefined,
  isBooted: false,
  setIsBooted: () => undefined,
  userHash: undefined,
  setUserHash: () => undefined,
});

export function IntercomProvider({
  children,
  ...props
}: PropsWithChildren<Omit<IntercomProviderProps, 'appId'>>) {
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const [isBooted, setIsBooted] = useState(false);
  const [userHash, setUserHash] = useState<string | undefined>(undefined);

  const value = useMemo<C>(
    () => ({
      unreadMessagesCount,
      setUnreadMessagesCount,
      isBooted,
      setIsBooted,
      userHash,
      setUserHash,
    }),
    [unreadMessagesCount, isBooted, userHash]
  );

  return (
    <_IntercomProvider
      appId={INTERCOM_APP_ID}
      onUnreadCountChange={setUnreadMessagesCount}
      {...props}
    >
      <intercomContext.Provider value={value}>
        {children}
      </intercomContext.Provider>
    </_IntercomProvider>
  );
}

export const useIntercom = () => {
  const intercom = _useIntercom();
  const {
    unreadMessagesCount,
    setUnreadMessagesCount,
    isBooted,
    setIsBooted,
    userHash,
    setUserHash,
  } = useContext(intercomContext);

  return {
    unreadMessagesCount,
    setUnreadMessagesCount,
    ...intercom,
    boot: useCallback(
      (...args: Parameters<typeof intercom.boot>) => {
        const newUserHash = args[0]?.userHash;
        if (!isBooted || userHash !== newUserHash) {
          // reboot intercom if userHash has changed
          if (userHash !== newUserHash) intercom.shutdown();
          intercom.boot(...args);

          setIsBooted(true);
          setUserHash(newUserHash);
        }
      },
      [
        intercom.boot,
        intercom.shutdown,
        isBooted,
        setIsBooted,
        userHash,
        setUserHash,
      ]
    ),
    shutdown: useCallback(
      (...args: Parameters<typeof intercom.shutdown>) => {
        if (isBooted) {
          intercom.shutdown(...args);
          setIsBooted(false);
        }
      },
      [intercom.shutdown, isBooted, setIsBooted]
    ),
    update: useCallback(
      (...args: Parameters<typeof intercom.update>) => {
        if (isBooted) intercom.update(...args);
      },
      [intercom.update, isBooted]
    ),
    trackEvent: useCallback(
      (...args: Parameters<typeof intercom.trackEvent>) => {
        if (isBooted) intercom.trackEvent(...args);
      },
      [intercom.trackEvent, isBooted]
    ),
  };
};
